/**
 * =========================================================================
 * 🎮 WEDDING APP CORE CONFIGURATION STORE
 * =========================================================================
 * Agent Instructions:
 * - This file powers the Journey Tab (the interactive board game).
 * - GAME_STEPS requires three arrays: p1 (Yisehak), p2 (Lydia), and joint.
 * - REQUIRED GAME_STEP PROPS: cx, cy, title, img, q, options, answer, props,
 *   ourStory.
 * - `title` names the checkpoint (e.g. "The Proposal"). The gallery captions
 *   every game photo with its checkpoint's title, so keep it short and keep
 *   the ANSWER OUT OF IT — the gallery is public and would spoil the game.
 * - For mini-games on the joint path: add `type` ('memory-match',
 *   'sliding-puzzle', 'jigsaw-puzzle') plus `puzzleImg`/`gridSize` or
 *   `matchImages`.
 * - memory-match steps use `matchImages` (one photo per pair — exactly 4
 *   pairs). Those photos are also appended to `images` so they show with the
 *   answers, and so every Cloudinary asset is used somewhere.
 * - Images live in Cloudinary under "Wedding App Photos/Game/...". Public IDs
 *   follow a role convention: avatar_* (checkpoint avatar), answer_* (answer
 *   reveal), memory_ (memory-match tile), sliding_ or jigsaw_ (puzzle source).
 *   Reference assets by public_id only; the helpers below build the URL.
 * =========================================================================
 */
const CLD = 'https://res.cloudinary.com/db1jhpo49/image/upload';
// f_auto lets Cloudinary pick the right format regardless of the source
// extension; g_auto keeps faces in frame on the square crops.
// q_auto lets Cloudinary serve a much smaller payload at no visible quality
// cost — important since these are full-resolution phone photos.
const avatarUrl = (id) => `${CLD}/w_240,h_240,c_fill,g_auto,f_auto,q_auto/${id}`;
const photoUrl = (id) => `${CLD}/w_1000,c_limit,f_auto,q_auto/${id}`;
const tileUrl = (id) => `${CLD}/w_500,h_500,c_fill,g_auto,f_auto,q_auto/${id}`;
const puzzleUrl = (id) => `${CLD}/w_800,h_800,c_fill,g_auto,f_auto,q_auto/${id}`;

export const GAME_STEPS = {
  p1: [
    {
      id: 'p1_0', cx: 400, cy: 300,
      title: "Yisehak's Family",
      img: avatarUrl('avatar_yisehak_vpedo5'),
      q: "How many siblings does Yisehak have?",
      options: ['2 brothers', '1 brother & 1 sister', '3 cousins', "He's an only child"],
      answer: '2 brothers',
      props: ['👨‍👩‍👦‍👦', '🏠', '❤️'],
      ourStory: "Yisehak grew up with two brothers — but he's always had a bigger family.",
      images: [
        photoUrl('answer_brothers_gua1sb'),
        photoUrl('answer_brothers_wqscbh'),
        photoUrl('answer_family_birthday_b3a2zr'),
        photoUrl('answer_family_puosvq'),
        photoUrl('answer_four_j1uipq'),
        photoUrl('answer_nahili_ldq4qh'),
        photoUrl('answer_parents_ilrpnu'),
        photoUrl('answer_parents_nbrwp9'),
        photoUrl('answer_parents_ojjmy7'),
        photoUrl('answer_roba_fejigf'),
        photoUrl('answer_roba_kena_z1w1rh'),
      ],
    },
    {
      id: 'p1_1', cx: 900, cy: 250,
      title: "Yisehak's First Pet",
      img: avatarUrl('avatar_us_gmpnks'),
      q: "What was the name of Yisehak's first pet?",
      options: ['Spooky', 'Dwight', 'Milo', 'Dimet'],
      answer: 'Spooky',
      props: ['🐕', '🐾', '🦴'],
      ourStory: "Spooky was Yisehak's first dog. Later on, he had dogs named Dwight and Milo.",
      images: [
        photoUrl('answer_baby_spooky_ykz7c2'),
        photoUrl('answer_spooky_nxpfhp'),
        photoUrl('answer_spooky_zdvmu7'),
        photoUrl('answer_spooky_cat_tnxq52'),
        photoUrl('answer_spooky_dwight_c5ppgh'),
        photoUrl('answer_smiling_dwight_xntzsj'),
        photoUrl('answer_dwight_cat_yy73qy'),
        photoUrl('answer_dwight_milo_eos9yo'),
        photoUrl('answer_three_dogs_x40bx9'),
      ],
    },
    {
      id: 'p1_2', cx: 1400, cy: 350,
      title: "Game Day",
      img: avatarUrl('avatar_jumping_d73y5k'),
      q: "Which sport does Yisehak love to play?",
      options: ['Football', 'Distance Running', 'Basketball', 'Tennis'],
      answer: 'Football',
      props: ['⚽', '🏃‍♂️', '👟'],
      ourStory: "Yisehak loves playing football ever since moving to London.",
      images: [
        photoUrl('answer_crossing_wectym'),
        photoUrl('answer_passing_fdnzxa'),
        photoUrl('answer_header_oxicqo'),
        photoUrl('answer_walking_omhkhh'),
        photoUrl('answer_lineup_yypmrb'),
      ],
    },
  ],
  p2: [
    {
      id: 'p2_0', cx: 400, cy: 600,
      title: "Lydia's Childhood",
      img: avatarUrl('avatar_baby_lily_qeagxa'),
      q: "What was the name of Lydia's childhood imaginary friend?",
      options: ['Kutana', 'Kukusha', 'Hiyab', 'Jackie'],
      answer: 'Kutana',
      props: ['👧', '✨', '👯‍♀️'],
      ourStory: "As a child, Lydia had an imaginary friend named Kutana. Later, when her sister was born, she said 'Yay, Kutana is here!'",
      images: [
        photoUrl('answer_babies_gx2az2'),
        photoUrl('answer_sisters_oidrsg'),
        photoUrl('answer_sisters_oclamo'),
        photoUrl('answer_sisters_plane_mgaaea'),
        photoUrl('answer_girls_fa7xtu'),
        photoUrl('answer_family_twgpun'),
        photoUrl('answer_family_omra6j'),
      ],
    },
    {
      id: 'p2_1', cx: 900, cy: 650,
      title: "Lydia's Favorite Book",
      img: avatarUrl('avatar_reading_hmauaa'),
      q: "What is Lydia's favorite book?",
      options: ['Harry Potter', 'Fiker eske Meqabir', "Hitchhiker's Guide to the Galaxy", 'Lord of the Rings'],
      answer: 'Harry Potter',
      props: ['⚡', '📚', '🧙‍♀️'],
      ourStory: "Lydia has read the entire Harry Potter series 4 times!",
      images: [
        photoUrl('answer_platform975_jq69hl'),
        photoUrl('answer_library_ufknpx'),
      ],
    },
    {
      id: 'p2_2', cx: 1400, cy: 550,
      title: "Lydia's Hidden Talent",
      img: avatarUrl('avatar_activity_dvp5oo'),
      q: "What is Lydia surprisingly good at?",
      options: ['Shooting', 'Baking', 'Parallel Parking', 'Chess'],
      answer: 'Shooting',
      props: ['🎯', '🔫', '🏆'],
      ourStory: "Lydia is surprisingly good at shooting! She was nailing targets on her first try, weather they are crosshairs, blinking lights, or flying clay!",
      images: [
        photoUrl('answer_airsoft_turkey_t1lega'),
        photoUrl('answer_airsoft_sniper_bfkdqs'),
        photoUrl('answer_arrow_ebr8nq'),
        photoUrl('answer_clays_vygnyl'),
        photoUrl('answer_clays_looking_cbuhjb'),
        photoUrl('answer_virtual_clay_1_zj5ilf'),
        photoUrl('answer_virtual_clay_r9ycrs'),
      ],
    },
  ],
  joint: [
    {
      id: 'j_0', type: 'sliding-puzzle', cx: 2000, cy: 450,
      title: "Where We Met",
      img: avatarUrl('avatar_throne_drrsfc'),
      puzzleImg: puzzleUrl('sliding_graduation_b7k4uo'),
      gridSize: 3,
      q: "Where did Yisehak and Lydia meet?",
      options: ['University', 'London', 'A Hackathon', 'A Coding Conference'],
      answer: 'University',
      props: ['🎓', '💻', '🎒'],
      ourStory: "We met in our early years at University, and grew even closer during the A2SV bootcamp.",
      images: [
        photoUrl('answer_a2sv_lmckt1'),
        photoUrl('answer_gulele_d9tjhq'),
        photoUrl('answer_photoshoot_uf3l8v'),
        photoUrl('answer_plane_lxruhv'),
        photoUrl('answer_cake_dv6qnx'),
        photoUrl('sliding_graduation_b7k4uo'),
      ],
    },
    {
      id: 'j_1', type: 'memory-match', cx: 2500, cy: 400,
      title: "Our Favorite Trip",
      img: avatarUrl('avatar_coffee_apki3i'),
      matchImages: [
        tileUrl('memory_boat_l7usi9'),
        tileUrl('memory_helicopter_rg8zdr'),
        tileUrl('memory_parasailing_lrgmq5'),
        tileUrl('memory_york_lwu17n'),
      ],
      q: "What was Yisehak and Lydia's favorite trip together?",
      options: ['Antalya', 'Barcelona', 'York', 'Bath'],
      answer: 'Antalya',
      props: ['✈️', '🏖️', '🌅'],
      ourStory: "Barcelona was beautiful, Bath was cosy, Edinburgh and York were a great time too, but the food in Antalya was unmatched! It was a time filled with fun activities!",
      images: [
        photoUrl('answer_us_eweesn'),
        photoUrl('answer_turkey_ilxpbo'),
        photoUrl('answer_edinburgh_kiau7u'),
        photoUrl('answer_edinburgh_walking_ytoxlc'),
        photoUrl('answer_canterbury_na1nd6'),
        photoUrl('answer_margate_sxcen2'),
        photoUrl('answer_cars_eluven'),
        photoUrl('memory_boat_l7usi9'),
        photoUrl('memory_helicopter_rg8zdr'),
        photoUrl('memory_parasailing_lrgmq5'),
        photoUrl('memory_york_lwu17n'),
      ],
    },
    {
      id: 'j_2', type: 'jigsaw-puzzle', cx: 3000, cy: 500,
      title: "Our Favorite Cake",
      img: avatarUrl('avatar_us_jh1p99'),
      puzzleImg: puzzleUrl('jigsaw_ring_zaklwr'),
      gridSize: 3,
      q: "What is Yisehak and Lydia's favorite cake?",
      options: ['Tres Leche', 'Tiramisu', 'Basque Cheesecake', 'Ice Cream'],
      answer: 'Tres Leche',
      props: ['🎂', '🍰', '🧁'],
      ourStory: "Tres Leche is our special cake. We used to bake it together (virtually) during COVID.",
      images: [
        photoUrl('answer_tres_leche_mcflrb'),
        photoUrl('answer_birthday_qdeh8n'),
        photoUrl('jigsaw_ring_zaklwr'),
      ],
    },
    {
      id: 'j_3', type: 'memory-match', cx: 3500, cy: 400,
      title: "The Proposal",
      img: avatarUrl('avatar_dark_gh06o1'),
      matchImages: [
        tileUrl('memory_baloon_ypfmdk'),
        tileUrl('memory_cake_xivmjg'),
        tileUrl('memory_high5_ssgz4l'),
        tileUrl('memory_us_je8fms'),
      ],
      q: "Where did Yisehak propose?",
      options: ['In the Clouds', 'Under the Sea', 'At a Beach at Sunset', 'In a Rooftop Restaurant'],
      answer: 'In the Clouds',
      props: ['🎈', '☁️', '💍'],
      ourStory: "Yisehak proposed in a hot air balloon, literally in the clouds of Winchester!",
      images: [
        photoUrl('answer_on_baloon_sjnz46'),
        photoUrl('answer_cutting_cake_qspywh'),
        photoUrl('answer_field_jzumpi'),
        photoUrl('answer_stairs_iqy5ub'),
        photoUrl('answer_us_vsytn9'),
        photoUrl('memory_baloon_ypfmdk'),
        photoUrl('memory_cake_xivmjg'),
        photoUrl('memory_high5_ssgz4l'),
        photoUrl('memory_us_je8fms'),
      ],
    },
  ],
};
export
const CLOUDS = Array.from({ length: 25 }).map((_, i) => ({
  id: i,
  cx: Math.random() * 4500,
  cy: Math.random() * 180 + 10,
  size: Math.random() * 140 + 100,
  type: Math.random() > 0.5 ? 1 : 2,
  opacity: Math.random() * 0.3 + 0.6
}));
export const GALLERY_ZONES = [
  { id: 'zone_1', title: 'Where We Met', cx: 300, cy: 450, r: 80, color: '#3b82f6', shape: 'circle', images: ['https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=800&h=600&fit=crop', 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800&h=600&fit=crop'] },
  { id: 'zone_4', title: 'Favorite Date Spot', cx: 450, cy: 800, r: 85, color: '#8b5cf6', shape: 'hexagon', images: ['https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=800&h=600&fit=crop', 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&h=600&fit=crop'] },
  { id: 'zone_2', title: 'First Trip', cx: 1000, cy: 250, r: 90, color: '#d946ef', shape: 'diamond', images: ['https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800&h=600&fit=crop', 'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=800&h=600&fit=crop'] },
  { id: 'zone_5', title: 'The Big Move', cx: 1350, cy: 450, r: 100, color: '#f59e0b', shape: 'star', images: ['https://images.unsplash.com/photo-1513694203232-719a280e022f?w=800&h=600&fit=crop', 'https://images.unsplash.com/photo-1484154218962-a197022b5858?w=800&h=600&fit=crop'] },
  { id: 'zone_6', title: 'Our First Home', cx: 850, cy: 950, r: 95, color: '#06b6d4', shape: 'square', images: ['https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=600&fit=crop', 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&h=600&fit=crop'] },
  { id: 'zone_3', title: 'The Proposal', cx: 1300, cy: 1000, r: 110, color: '#f43f5e', shape: 'star', images: ['https://images.unsplash.com/photo-1515934751635-c81c6bc9a2d8?w=800&h=600&fit=crop', 'https://images.unsplash.com/photo-1522673607200-164d1b6ce486?w=800&h=600&fit=crop'] }
];

// ==========================================
// 2. STYLES & HELPERS
// ==========================================
export const DEFAULT_GAME_STATE = { p1Step: 0, p2Step: 0, jointStep: -1, mistakes: 0, status: 'playing' };
export const PATH_P1 = "M 400 300 C 600 300, 700 250, 900 250 C 1100 250, 1200 350, 1400 350 C 1600 350, 1800 450, 2000 450";
export const PATH_P2 = "M 400 600 C 600 600, 700 650, 900 650 C 1100 650, 1200 550, 1400 550 C 1600 550, 1800 450, 2000 450";
export const PATH_JOINT = "M 2000 450 Q 2250 450 2500 400 T 3000 500 T 3500 400 T 4000 450";
